import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { useAuthStore } from "@/stores/auth.store";
import { useNavigate } from "react-router-dom";
import {
  Coins,
  DollarSign,
  ArrowDownRight,
  ArrowUpRight,
  ArrowRightLeft,
  PiggyBank,
  LayoutDashboard,
  Loader2,
  TrendingUp,
  ChevronRight,
  Shield,
} from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { companyApi, hedgesCompanyApi, type Hedge } from "@/services/api";
import { WalletBadge } from "@/components/company/WalletBadge";
import { DepositModal } from "@/components/company/DepositModal";
import { OpenFinanceCTA } from "@/components/company/OpenFinanceCTA";

interface DashboardData {
  balance: {
    brld: string;
    brldWei: string;
    brl: number;
    yield: number;
    availableBalance: number;
    appliedBalance: number;
    usdcBalance: number;
    usdcLockedBalance: number;
  };
  position: {
    totalPrincipal: number;
    totalCurrent: number;
    yieldAmount: number;
    accumulatedFactor: number;
    businessDays: number;
  };
  product: {
    cdiRate: number;
    annualRate: number;
  };
  wallet: {
    address: string;
    networkId: string;
    networkName: string;
    explorerUrl: string;
  };
  animation: {
    nextBusinessDayAt: string;
    currentDailyRate: number;
    secondsUntilNextBusinessDay: number;
  };
}

// Formata USDC
function formatUSDC(value: number): string {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function CompanyDashboardPage() {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [activeHedges, setActiveHedges] = useState<Hedge[]>([]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const data = await companyApi.dashboard.get();
      setDashboardData(data);
    } catch (error) {
      console.error("Erro ao carregar dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadActiveHedges = async () => {
    try {
      const data = await hedgesCompanyApi.listHedges({ status: "ACTIVE", limit: 10 });
      setActiveHedges(data.items || []);
    } catch {
      // Silencioso - funcionalidade opcional
    }
  };

  useEffect(() => {
    loadDashboard();
    loadActiveHedges();
  }, []);

  const balance = dashboardData?.balance || {
    brld: "0",
    brl: 0,
    yield: 0,
    availableBalance: 0,
    appliedBalance: 0,
    usdcBalance: 0,
    usdcLockedBalance: 0,
  };
  const position = dashboardData?.position || {
    totalPrincipal: 0,
    totalCurrent: 0,
    yieldAmount: 0,
    accumulatedFactor: 1,
    businessDays: 0,
  };
  const product = dashboardData?.product || { cdiRate: 95, annualRate: 12.59 };
  const wallet = dashboardData?.wallet;

  // Calcular valores
  const availableBalanceReais = balance.availableBalance / 100;
  const appliedBalanceReais = balance.appliedBalance / 100;
  const yieldAmount = position.yieldAmount || 0;
  const totalBrld = availableBalanceReais + appliedBalanceReais + yieldAmount;
  const usdcBalanceUsd = balance.usdcBalance / 100;
  const usdcLockedUsd = (balance.usdcLockedBalance || 0) / 100;
  const usdcAvailableUsd = usdcBalanceUsd - usdcLockedUsd;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<LayoutDashboard className="w-5 h-5 sm:w-6 sm:h-6" />}
        title={`Bem-vindo, ${user?.name?.split(" ")[0] || ""}!`}
        description={user?.companyName || "Sua conta digital"}
        stats={[
          {
            value: loading ? "..." : formatCurrency(totalBrld),
            label: "BRLD",
          },
          {
            value: loading ? "..." : formatUSDC(usdcBalanceUsd),
            label: "USDC",
          },
        ]}
        actions={
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            {wallet && (
              <WalletBadge address={wallet.address} explorerUrl={wallet.explorerUrl} />
            )}
          </div>
        }
      />

      {/* Cards de Saldo lado a lado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Card BRLD */}
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate("/brld")}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Coins className="w-5 h-5 text-primary" />
                BRLD - Real Digital
              </CardTitle>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            ) : (
              <>
                <div>
                  <p className="text-3xl font-bold">{formatCurrency(totalBrld)}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {availableBalanceReais > 0 && (
                      <span>{formatCurrency(availableBalanceReais)} disponível</span>
                    )}
                    {availableBalanceReais > 0 && appliedBalanceReais > 0 && " · "}
                    {appliedBalanceReais > 0 && (
                      <span className="text-green-600">{formatCurrency(appliedBalanceReais)} aplicado</span>
                    )}
                    {yieldAmount > 0 && (
                      <>
                        {(availableBalanceReais > 0 || appliedBalanceReais > 0) && " · "}
                        <span className="text-green-600">+{formatCurrency(yieldAmount)} rendimento</span>
                      </>
                    )}
                  </p>
                </div>

                {appliedBalanceReais > 0 && (
                  <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">
                      Rendendo {product.cdiRate}% do CDI ({product.annualRate.toFixed(2)}% a.a.)
                    </span>
                  </div>
                )}

                {availableBalanceReais > 0 && appliedBalanceReais === 0 && (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
                    <PiggyBank className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-700">
                      Saldo não está rendendo. Aplique para ganhar CDI!
                    </span>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Card USDC */}
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate("/usdc")}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-blue-500" />
                USDC - Dólar Digital
              </CardTitle>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            ) : (
              <>
                <div>
                  <p className="text-3xl font-bold">{formatUSDC(usdcBalanceUsd)}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {usdcLockedUsd > 0 ? (
                      <>
                        <span>{formatUSDC(usdcAvailableUsd)} disponível</span>
                        <span> · </span>
                        <span className="text-blue-600">
                          <Shield className="w-3 h-3 inline mr-0.5" />
                          {formatUSDC(usdcLockedUsd)} em hedge
                        </span>
                      </>
                    ) : (
                      "Stablecoin pareada ao dólar"
                    )}
                  </p>
                </div>

                {usdcBalanceUsd > 0 && usdcLockedUsd === 0 && (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
                    <DollarSign className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-700">
                      USDC não rende. Converta para BRLD e aplique!
                    </span>
                  </div>
                )}

                {usdcBalanceUsd === 0 && (
                  <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                    <ArrowRightLeft className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Compre USDC convertendo seu BRLD
                    </span>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Card Proteção Ativa */}
      {activeHedges.length > 0 && (
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow border-green-200"
          onClick={() => navigate("/protecao-cambial")}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                Proteção Cambial
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="success">Proteção ativa</Badge>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activeHedges.map((hedge) => {
                const daysRemaining = Math.max(
                  0,
                  Math.ceil(
                    (new Date(hedge.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                  )
                );
                return (
                  <div
                    key={hedge.id}
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg"
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{hedge.hedgeCode}</p>
                      <p className="text-xs text-muted-foreground">
                        Valor protegido: {formatUSDC(parseFloat(hedge.hedgeAmountUsdc || "0"))}
                      </p>
                    </div>
                    <div className="text-right space-y-1">
                      <p className="text-sm font-medium text-green-700">
                        {parseFloat(hedge.lockedAmountBrl || "0").toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {daysRemaining} {daysRemaining === 1 ? "dia restante" : "dias restantes"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grid de Ações Rápidas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => setShowDepositModal(true)}
        >
          <ArrowDownRight className="w-6 h-6 text-green-600" />
          <span className="text-sm font-medium">Depositar</span>
          <span className="text-xs text-muted-foreground">Via PIX</span>
        </Button>

        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate("/aplicar")}
        >
          <PiggyBank className="w-6 h-6 text-primary" />
          <span className="text-sm font-medium">Aplicar</span>
          <span className="text-xs text-muted-foreground">Render CDI</span>
        </Button>

        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate("/converter")}
        >
          <ArrowRightLeft className="w-6 h-6 text-blue-600" />
          <span className="text-sm font-medium">Converter</span>
          <span className="text-xs text-muted-foreground">BRLD ↔ USDC</span>
        </Button>

        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate("/brld")}
        >
          <ArrowUpRight className="w-6 h-6 text-orange-600" />
          <span className="text-sm font-medium">Resgatar</span>
          <span className="text-xs text-muted-foreground">Para conta</span>
        </Button>
      </div>

      {/* CTA Open Finance */}
      <OpenFinanceCTA />

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>Suas moedas digitais</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4 text-sm text-muted-foreground">
          <div className="flex gap-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Coins className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-foreground">BRLD - Real Digital</p>
              <p className="text-xs sm:text-sm">
                Stablecoin 1:1 com o Real, lastreada em Tesouro Selic. Pode render {product.cdiRate}% do CDI quando aplicado.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-4 h-4 text-blue-500" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-foreground">USDC - Dólar Digital</p>
              <p className="text-xs sm:text-sm">
                Stablecoin 1:1 com o Dólar americano, emitida pela Circle. Não possui rendimento.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <ArrowRightLeft className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-foreground">Conversão instantânea</p>
              <p className="text-xs sm:text-sm">
                Converta entre BRLD e USDC a qualquer momento com cotação em tempo real.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Depósito */}
      <DepositModal open={showDepositModal} onClose={() => setShowDepositModal(false)} />
    </div>
  );
}
